// src/views/subscription/SubscriptionExpiredMember.js - VERSION CORRIGÉE

import React, { useState, useEffect } from 'react'
import { CContainer, CCard, CCardBody, CAlert, CSpinner, CBadge } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilInfo, cilCheckCircle, cilWarning, cilReload } from '@coreui/icons'
import { stripeService } from '../../services/stripeService'

const SubscriptionExpiredMember = () => {
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false) // ✅ NOUVEAU - État pour le bouton
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [error, setError] = useState(null)
  const [lastCheck, setLastCheck] = useState(null) // ✅ NOUVEAU - Heure dernière vérif

  useEffect(() => {
    checkSubscriptionStatus()
    const interval = setInterval(checkSubscriptionStatus, 60000)
    return () => clearInterval(interval)
  }, [])

  const checkSubscriptionStatus = async () => {
    try {
      // Si c'est un check manuel, utiliser setChecking, sinon setLoading
      if (!loading) {
        setChecking(true)
      }

      setError(null) // ✅ Réinitialiser l'erreur

      const { subscription } = await stripeService.getCurrentSubscription()

      if (!subscription) {
        setSubscriptionStatus('no_subscription')
        setLoading(false)
        setChecking(false)
        return
      }

      // Vérifier si l'accès est autorisé
      const activeStatuses = ['active', 'trialing']
      const isPastDueWithGrace =
        subscription.status === 'past_due' &&
        subscription.grace_until &&
        new Date(subscription.grace_until) > new Date()

      if (
        activeStatuses.includes(subscription.status) ||
        subscription.plan === 'sponsored' ||
        isPastDueWithGrace
      ) {
        // L'abonnement est valide, rediriger vers le dashboard
        console.log('✅ Abonnement valide détecté, redirection...')
        window.location.href = '/dashboard'
        return
      }

      setSubscriptionStatus(subscription.status)
      setLastCheck(new Date()) // ✅ Mettre à jour l'heure
      setLoading(false)
      setChecking(false)
    } catch (err) {
      console.error('Erreur vérification statut:', err)
      setError("Impossible de vérifier le statut de l'abonnement")
      setLoading(false)
      setChecking(false)
    }
  }

  // ✅ NOUVEAU - Fonction pour formater l'heure
  const formatLastCheck = () => {
    if (!lastCheck) return ''
    return lastCheck.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  if (loading) {
    return (
      <CContainer className="mt-4 text-center">
        <CSpinner color="primary" size="lg" />
        <p className="mt-3 text-medium-emphasis">Vérification du statut...</p>
      </CContainer>
    )
  }

  if (error) {
    return (
      <CContainer className="mt-4">
        <CAlert color="danger">
          <CIcon icon={cilWarning} className="me-2" />
          {error}
        </CAlert>
        {/* ✅ Bouton retry en cas d'erreur */}
        <div className="text-center">
          <button
            className="btn btn-outline-danger"
            onClick={checkSubscriptionStatus}
            disabled={checking}
          >
            {checking ? <CSpinner size="sm" className="me-2" /> : '🔄 '}
            Réessayer
          </button>
        </div>
      </CContainer>
    )
  }

  return (
    <CContainer className="mt-4">
      <style>{`
        .check-button {
          position: relative;
          overflow: hidden;
        }
        
        .check-button.checking::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          animation: shimmer 1s infinite;
        }
        
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
        
        .last-check-badge {
          font-size: 12px;
          padding: 4px 8px;
        }
      `}</style>

      <CCard className="shadow-sm">
        <CCardBody className="p-5 text-center">
          {/* Icône */}
          <div
            className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10"
            style={{ width: '100px', height: '100px' }}
          >
            <CIcon icon={cilInfo} size="4xl" className="text-warning" />
          </div>

          {/* Titre */}
          <h2 className="mb-3 text-warning">Service temporairement limité</h2>

          {/* Message principal */}
          <CAlert color="warning" className="text-start mb-4">
            <div className="d-flex align-items-start">
              <CIcon icon={cilWarning} className="me-2 mt-1" />
              <div>
                <strong>Information</strong>
                <p className="mb-0 mt-2">
                  Votre organisation est en train de mettre à jour son abonnement. Certaines
                  fonctionnalités sont temporairement limitées.
                </p>
                {subscriptionStatus && (
                  <p className="mb-0 mt-2">
                    <small className="text-muted">
                      Statut actuel : <CBadge color="warning">{subscriptionStatus}</CBadge>
                    </small>
                  </p>
                )}
              </div>
            </div>
          </CAlert>

          {/* Description */}
          <div className="text-muted mb-4">
            <p>
              Les administrateurs de votre organisation travaillent actuellement sur la
              régularisation de l'abonnement.
            </p>
            <p className="mb-0">
              Vous pourrez accéder à toutes les fonctionnalités dès que l'abonnement sera réactivé.
            </p>
          </div>

          {/* Que faire */}
          <CCard className="bg-light border-0 mb-4">
            <CCardBody>
              <h5 className="mb-3">💡 Que faire ?</h5>
              <ul className="list-unstyled mb-0 text-start">
                <li className="mb-2">✉️ Contactez un administrateur de votre organisation</li>
                <li className="mb-2">
                  ⏰ Patientez pendant la mise à jour (vérification automatique toutes les 30s)
                </li>
              </ul>
            </CCardBody>
          </CCard>

          {/* ✅ Bouton refresh manuel amélioré */}
          <div className="mb-4">
            <button
              className={`btn btn-outline-primary check-button ${checking ? 'checking' : ''}`}
              onClick={checkSubscriptionStatus}
              disabled={checking}
              style={{ minWidth: '200px' }}
            >
              {checking ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Vérification...
                </>
              ) : (
                <>
                  <CIcon icon={cilReload} className="me-2" />
                  Vérifier maintenant
                </>
              )}
            </button>

            {/* ✅ NOUVEAU - Afficher l'heure de la dernière vérification */}
            {lastCheck && !checking && (
              <div className="mt-2">
                <CBadge color="light" className="last-check-badge">
                  <CIcon icon={cilCheckCircle} size="sm" className="me-1" />
                  Dernière vérification : {formatLastCheck()}
                </CBadge>
              </div>
            )}
          </div>

          {/* Support */}
          <div className="mt-4 pt-3 border-top">
            <small className="text-muted">
              Besoin d'aide urgente ?{' '}
              <a
                href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL || 'thibault@terrilink.io'}`}
              >
                Contactez le support
              </a>
            </small>
          </div>
        </CCardBody>
      </CCard>
    </CContainer>
  )
}

export default SubscriptionExpiredMember
